/**
 * Centralized constants for the application
 * Single source of truth for Cognito attributes, API names, and configuration
 */

// ============================================================================
// COGNITO USER ATTRIBUTE KEYS
// ============================================================================

/**
 * Cognito user's unique identifier (sub)
 * AWS Cognito standard attribute
 */
export const COGNITO_SUB = "sub";

/**
 * Cognito user's email address
 * AWS Cognito standard attribute
 */
export const COGNITO_EMAIL = "email";

/**
 * Cognito searchable subscription ID
 * Maps to preferred_username field
 */
export const COGNITO_PREFERRED_USERNAME = "preferred_username";

// ============================================================================
// STRIPE CUSTOM ATTRIBUTES (stored in Cognito)
// ============================================================================

/**
 * Stripe customer ID
 * Custom Cognito attribute
 */
export const STRIPE_CUSTOMER_ID = "custom:stripeId";

/**
 * Stripe subscription expiry timestamp (Unix epoch)
 * Custom Cognito attribute
 */
export const STRIPE_SUBSCRIPTION_EXPIRY = "custom:stripeSubExpiry";

/**
 * Stripe subscription status (ACTIVE, INACTIVE, PAUSED, etc.)
 * Custom Cognito attribute
 */
export const STRIPE_SUBSCRIPTION_STATUS = "custom:slugSubscrStatus";

/**
 * Stripe subscription ID
 * Custom Cognito attribute
 */
export const STRIPE_SUBSCRIPTION_ID = "custom:subscriptionId";

// ============================================================================
// COGNITO AUTH EVENTS
// ============================================================================

/**
 * Cognito sign-in event name
 */
export const COGNITO_EVENT_SIGNIN = "signIn";

/**
 * Cognito auto sign-in event name
 */
export const COGNITO_EVENT_AUTOSIGNIN = "autoSignIn";

// ============================================================================
// API ENDPOINT NAMES (AWS Amplify API names)
// ============================================================================

/**
 * Rekognition API - REMOVED
 * Face matching now uses direct fetch - see src/services/api/rekognitionService.js
 * Admin rebuild functionality commented out until migrated to new architecture
 */
// export const API_REKOGNITION = "rekognitionapi"; // Commented out - admin functionality disabled

/**
 * User event API endpoint name
 * Used for event CRUD operations
 */
export const API_USER_EVENT = "usereventapi";

/**
 * User events aggregation API endpoint name
 * Used for querying aggregated events (operator console)
 */
export const API_USER_EVENTS_AGGREGATION = "usereventsaggregation";

/**
 * Stripe API endpoint name
 * Used for subscription management
 */
export const API_STRIPE = "stripeuserapi";

/**
 * User management API endpoint name
 * Used for Cognito user operations
 */
export const API_USER_MANAGEMENT = "sluguserapi";

/**
 * Tenant configuration API endpoint name
 * Used for managing tenant-specific settings (clustering, etc.)
 */
export const API_TENANT_CONFIG = "tenantconfigapi";

// ============================================================================
// COGNITO USER GROUPS
// ============================================================================

/**
 * Administrator group name
 */
export const GROUP_ADMINISTRATOR = "ADMIN";

/**
 * Moderator group name
 */
export const GROUP_MODERATOR = "MODERATOR";

/**
 * Operator group name
 */
export const GROUP_OPERATOR = "OPERATOR";

/**
 * Standard user group name
 */
export const GROUP_STANDARD = "STANDARD";

/**
 * Guest group name (for unauthenticated/limited access)
 */
export const GROUP_GUEST = "GUEST";

// ============================================================================
// USER ROLES
// ============================================================================

/**
 * User role enum values
 */
export const ROLE_ADMIN = 'ADMIN';
export const ROLE_MODERATOR = 'MODERATOR';
export const ROLE_OPERATOR = 'OPERATOR';
export const ROLE_STANDARD = 'STANDARD';
export const ROLE_GUEST = 'GUEST';
